/**
 * Hariom ERP - Sidebar Navigation
 * Left sidebar with module navigation, company logo, and user info
 * Responsive: collapses on mobile
 */
import React, { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, TrendingUp, Package, Factory, ShoppingCart, ShoppingBag,
  CheckCircle2, DollarSign, Users, BarChart3, Settings, Gem, Boxes, ClipboardList,
  ChevronLeft, ChevronRight, LogOut, Bell, X
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import clsx from 'clsx'

// ===== NAVIGATION MENU ITEMS =====
const NAV_ITEMS = [
  {
    group: 'Main',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    group: 'Operations',
    items: [
      { path: '/forecast',   label: 'Demand Forecast', icon: TrendingUp },
      { path: '/purchase',   label: 'Purchase',        icon: ShoppingBag },
      { path: '/sales-order', label: 'Sales Order',    icon: Gem },
      { path: '/diamond-purchase', label: 'Diamond Purchase', icon: Boxes },
      { path: '/debtor',     label: 'Debtor Report',   icon: ClipboardList },
      { path: '/inventory',  label: 'Inventory',       icon: Package },
      { path: '/production', label: 'Production',      icon: Factory },
      { path: '/sales',      label: 'Sales & CRM',     icon: ShoppingCart },
      { path: '/quality',    label: 'Quality Control', icon: CheckCircle2 },
    ],
  },
  {
    group: 'Management',
    items: [
      { path: '/finance',  label: 'Finance',  icon: DollarSign },
      { path: '/hr',       label: 'HR',       icon: Users },
      { path: '/reports',  label: 'Reports',  icon: BarChart3 },
      { path: '/settings', label: 'Settings', icon: Settings },
    ],
  },
]

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const [collapsed, setCollapsed] = useState(false)
  const { user, logout } = useAuthStore()
  const location = useLocation()

  // Restricted roles see only their allowed page(s); everyone else sees the full menu.
  const ALLOWED: Record<string, string[]> = {
    marketing: ['/forecast'],
    purchase: ['/purchase'],
    sales: ['/sales-order', '/diamond-purchase'],
    salesorder: ['/sales-order'],
    accountant: ['/debtor'],
  }
  const allowedPaths = user ? ALLOWED[user.role] : undefined
  const navGroups = allowedPaths
    ? [{ group: 'Operations', items: NAV_ITEMS.flatMap(g => g.items).filter(i => allowedPaths.includes(i.path)) }]
    : NAV_ITEMS

  return (
    <>
      {/* ===== Mobile Overlay ===== */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* ===== Sidebar Panel ===== */}
      <aside
        className={clsx(
          'fixed top-0 left-0 h-full bg-white shadow-sidebar z-50 transition-all duration-300 flex flex-col',
          // Desktop: always visible, collapsible
          'hidden lg:flex',
          collapsed ? 'lg:w-16' : 'lg:w-64',
          // Mobile: slide in/out
          isOpen ? 'flex !w-64' : 'hidden'
        )}
      >
        {/* ===== Logo / Brand Header ===== */}
        <div className="gradient-brand p-4 flex items-center justify-between min-h-[64px]">
          {!collapsed && (
            <div className="flex items-center gap-2">
              {/* Company logo text */}
              <div className="bg-white rounded-lg p-1.5 flex items-center justify-center">
                <span className="text-primary-600 font-black text-sm leading-none">H</span>
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-tight">Hariom</p>
                <p className="text-red-200 text-xs leading-tight">EpoxyShield ERP</p>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="bg-white rounded-lg p-1.5 mx-auto">
              <span className="text-primary-600 font-black text-sm">H</span>
            </div>
          )}

          {/* Mobile close button */}
          <button onClick={onClose} className="lg:hidden text-white/80 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>

        {/* ===== Navigation Links ===== */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          {navGroups.map((group) => (
            <div key={group.group} className="mb-4">
              {/* Group label (hidden when collapsed) */}
              {!collapsed && (
                <p className="text-xs font-semibold text-steel-400 uppercase tracking-wider px-3 mb-1">
                  {group.group}
                </p>
              )}
              {group.items.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname.startsWith(item.path)
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    title={collapsed ? item.label : undefined}
                    className={clsx(
                      'nav-link mb-0.5',
                      isActive && 'nav-link-active',
                      collapsed && 'justify-center px-0'
                    )}
                  >
                    {/* Navigation icon */}
                    <Icon size={18} className={clsx(isActive ? 'text-primary-600' : 'text-steel-500')} />
                    {/* Label hidden when sidebar is collapsed */}
                    {!collapsed && (
                      <span className="flex-1">{item.label}</span>
                    )}
                    {/* Active indicator dot */}
                    {isActive && !collapsed && (
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                    )}
                  </NavLink>
                )
              })}
            </div>
          ))}
        </nav>

        {/* ===== User Profile + Logout ===== */}
        <div className="border-t border-steel-100 p-3">
          {!collapsed && user && (
            <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-steel-50">
              {/* User avatar initials */}
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <span className="text-primary-700 font-semibold text-xs">
                  {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-steel-800 truncate">{user.name}</p>
                <p className="text-xs text-steel-500 capitalize truncate">
                  {user.role.replace(/_/g, ' ')}
                </p>
              </div>
            </div>
          )}

          {/* Logout button */}
          <button
            onClick={logout}
            className={clsx(
              'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm',
              'text-steel-600 hover:bg-red-50 hover:text-red-600 transition-colors',
              collapsed && 'justify-center'
            )}
          >
            <LogOut size={16} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>

        {/* ===== Collapse Toggle (Desktop only) ===== */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-white border
                     border-steel-200 rounded-full items-center justify-center
                     text-steel-500 hover:text-steel-700 hover:bg-steel-50 shadow-sm"
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* ===== Mobile Sidebar (same as above but always rendered for animation) ===== */}
      <aside
        className={clsx(
          'fixed top-0 left-0 h-full bg-white shadow-sidebar z-50 transition-transform duration-300 flex flex-col w-64 lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Duplicate inner content for mobile */}
        <div className="gradient-brand p-4 flex items-center justify-between min-h-[64px]">
          <div className="flex items-center gap-2">
            <div className="bg-white rounded-lg p-1.5">
              <span className="text-primary-600 font-black text-sm">H</span>
            </div>
            <div>
              <p className="text-white font-bold text-sm">Hariom</p>
              <p className="text-red-200 text-xs">EpoxyShield ERP</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2">
          {navGroups.map((group) => (
            <div key={group.group} className="mb-4">
              <p className="text-xs font-semibold text-steel-400 uppercase tracking-wider px-3 mb-1">
                {group.group}
              </p>
              {group.items.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname.startsWith(item.path)
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className={clsx('nav-link mb-0.5', isActive && 'nav-link-active')}
                  >
                    <Icon size={18} className={isActive ? 'text-primary-600' : 'text-steel-500'} />
                    <span className="flex-1">{item.label}</span>
                    {isActive && <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />}
                  </NavLink>
                )
              })}
            </div>
          ))}
        </nav>

        <div className="border-t border-steel-100 p-3">
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-steel-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  )
}

export default Sidebar
